// What a handler='open' action button opens, drawn on the platform.
//
// The same panes the Run view and the designer's Live Preview use for each
// object type, handed the values the button resolved from the row it was
// clicked on. Used inside the drawer (presentation 'drawer', on either
// surface); the Run view's own object pane passes the same values when the
// button navigates there instead.

import { lazy, Suspense } from 'react'
import type { OpenScreenPayload } from '@twasta/page-runtime/txn'
import { GenProcessLivePreviewPane } from '@/pages/designers/genprocess/RunPanel'
import { VisualLivePreviewPane } from '@/pages/designers/visual/VisualPreviewModal'
import { DashboardLivePreviewPane } from '@/pages/designers/dashboard/DashboardPreviewModal'

// Lazy: the transaction pane is the screen that OPENED this one, and importing
// it eagerly here would make the two modules import each other.
const LivePreviewLazyPane = lazy(() => import('@/pages/livepreview/LivePreviewPage'))
// Lazy for the same reason: the wizard host opens its own objects in this pane.
const PlatformWizardLazy = lazy(() => import('./wizardHost').then((m) => ({ default: m.PlatformWizard })))

export default function OpenObjectPane({
  project, payload, openKey, layoutMode, onEditorChange,
}: {
  project: string
  payload: OpenScreenPayload
  /** Makes the transaction run the payload once (the hand-off / click id). */
  openKey: string
  layoutMode: 'design' | 'customize'
  /** A transaction target's record editor opened / closed. */
  onEditorChange?: (s: { open: boolean; txnId: string | null }) => void
}) {
  if (payload.objType === 'T') {
    return (
      <Suspense fallback={<div style={{ padding: 24 }}><div className="spinner" /></div>}>
        <LivePreviewLazyPane
          project={project}
          objName={payload.objName}
          layoutMode={layoutMode}
          initialScreen={{ open: payload, openKey }}
          onEditorChange={onEditorChange}
        />
      </Suspense>
    )
  }
  // A Transaction Wizard: its params are the wizard's launch inputs.
  if ((payload.objType as string) === 'U') {
    return (
      <Suspense fallback={<div style={{ padding: 24 }}><div className="spinner" /></div>}>
        <PlatformWizardLazy
          project={project}
          wizard={payload.objName}
          inputs={payload.params}
          presentation="drawer"
        />
      </Suspense>
    )
  }
  if (payload.objType === 'G') {
    return (
      <GenProcessLivePreviewPane
        project={project}
        objName={payload.objName}
        runMode
        initialCriteria={payload.params}
        protectedCriteria={payload.protectedParams}
      />
    )
  }
  if (payload.objType === 'D') {
    return <DashboardLivePreviewPane project={project} objName={payload.objName} initialGlobals={payload.params} />
  }
  if (payload.objType === 'V') {
    return <VisualLivePreviewPane project={project} objName={payload.objName} initialParams={payload.params} />
  }
  return (
    <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14 }}>
      This kind of object cannot be opened here.
    </div>
  )
}
